import { ModalGeneric } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import ProgramPointList from '../../../../WorkReportModals/ProgramPointModal/ProgramPointList/ProgramPointList';
import useToast from '../../../../../hooks/useToast';
import {
  getOperationalDetails,
  getProgramPoints,
  putApproveDailyWorkReports
} from '../../../../../services/workReport';
import adapterDefaultValueCS from '../../../../../adapters/defaultValueCodeSubCode/defaultValueCSAdapter';

const validationSchema = Yup.object().shape({
  programPoints: Yup.array().of(
    Yup.object().shape({
      quantity: Yup.string().required('La cantidad es requerida'),
      points: Yup.string(),
      idPartePuntoPrograma: Yup.number().nullable(),
      idParteDiario: Yup.number().nullable(),
      subCode: Yup.object().required('El subcódigo es requerido'),
      code: Yup.object().required('El código es requerido'),
      cost: Yup.object().required('El costo es requerido')
    })
  ),
  totalValue: Yup.number().required('El valor total es requerido')
});

const hasInvalidProgramPoint = (programPoint) =>
  programPoint?.code === null ||
  programPoint?.code === '' ||
  programPoint?.subCode === null ||
  programPoint?.subCode === '' ||
  programPoint?.cost === null ||
  programPoint?.cost === '' ||
  programPoint?.cost?.nombre === '' ||
  programPoint?.code?.nombre === '' ||
  programPoint?.subCode?.nombre === '';

const mapProgramPoint = (
  {
    cantidad,
    claseCosto,
    codigo,
    descripcion,
    id,
    idOperativo,
    idParteTrabajoDiario,
    puntoDePrograma,
    subcodigo
  },
  line
) => ({
  quantity: cantidad || line?.cantidad || '',
  points: puntoDePrograma || '',
  idPartePuntoPrograma: id,
  idParteDiario: idParteTrabajoDiario || line?.idParteDiario,
  cost: claseCosto
    ? {
        costoClase: claseCosto?.split('-')[0],
        id: claseCosto,
        nombre: claseCosto,
        label: claseCosto,
        fase: claseCosto?.split('-')[1]
      }
    : null,
  code: claseCosto ? { nombre: codigo, id: codigo, label: codigo } : null,
  subCode: subcodigo ? { nombre: subcodigo, id: subcodigo, label: subcodigo } : null,
  detail: descripcion,
  idOperativo
});

const getCosts = async (line, daily) =>
  adapterDefaultValueCS({
    id: line?.idParteDiario,
    body: {
      tipoAsignacion: daily?.tipoAsignacion,
      numeroContrato: daily?.numeroContrato,
      numeroPosicion: Number(daily?.posicionSap.split(' ')[0]),
      numeroLinea: Number(daily?.lineaSap.split(' ')[0])
    }
  });

const getDescription = async (line, costs, daily) => {
  if (costs?.cost?.costoClase && costs?.cost?.fase && costs?.code?.id && costs?.subCode?.id) {
    const body = {
      tipoAsignacion: daily?.tipoAsignacion,
      claseCosto: costs?.cost?.costoClase.trim(),
      fase: costs?.cost?.fase.trim(),
      codigo: costs?.code.id,
      subcodigo: costs?.subCode.id
    };
    const response = await getOperationalDetails(line?.idParteDiario, body);

    return response?.descripcion;
  }

  return null;
};

const fetchProgramPoints = async (line) => {
  const res = await getProgramPoints(line?.idParteDiario);

  return res?.map((pp) => mapProgramPoint(pp, line));
};

const calculateTotalValue = (adapterPP) =>
  adapterPP?.reduce((acc, curr) => acc + Number(curr.quantity), 0) || 0;

const updateAdapterPP = (adapterPP, costs, descripcion, line) => {
  if (adapterPP.length > 0) {
    // eslint-disable-next-line no-param-reassign
    adapterPP[0] = {
      ...adapterPP[0],
      cost: costs?.cost,
      code: costs?.code,
      subCode: costs?.subCode,
      idParteDiario: line?.idParteDiario,
      detail: descripcion
    };
  } else {
    adapterPP.push({
      ...adapterPP[0],
      quantity: line?.cantidad,
      idParteDiario: line?.idParteDiario,
      cost: costs?.cost,
      code: costs?.code,
      subCode: costs?.subCode,
      detail: descripcion
    });
  }
};

const ModalProgramPointApproval = ({ show, onHide, ...props }) => {
  const { line, dateSelect, fetchData, editCSA, daily, defaultValue } = props;

  const { setNewToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [quantityTotalReturn, setQuantityTotalReturn] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const { values, setFieldValue, resetForm } = useFormik({
    initialValues: {
      programPoints: [
        {
          quantity: '',
          points: '',
          idPartePuntoPrograma: null,
          idParteDiario: null,
          code: null,
          subCode: null,
          cost: null,
          detail: null,
          options: []
        }
      ],
      totalValue: 0
    },
    validationSchema
  });

  const onExit = () => {
    onHide();
    fetchData();
    resetForm();
  };

  const hasRequiredProperties = (PP) =>
    Object.prototype.hasOwnProperty.call(PP, 'code') &&
    Object.prototype.hasOwnProperty.call(PP, 'subCode') &&
    Object.prototype.hasOwnProperty.call(PP, 'cost');

  useEffect(() => {
    const hasCompleteData = values.programPoints.every(hasRequiredProperties);

    if (hasCompleteData) {
      setShowAlert(values.programPoints.some(hasInvalidProgramPoint));
    } else {
      setShowAlert(true);
    }
  }, [values.programPoints]);

  useEffect(() => {
    const getDataPP = async () => {
      try {
        setLoading(true);

        const adapterPP = await fetchProgramPoints(line);

        const totalValue = calculateTotalValue(adapterPP);

        await setFieldValue('totalValue', totalValue);
        setQuantityTotalReturn(totalValue);

        let costs;

        if (!daily?.variosValoresOperativos && !daily?.codigoOperativo && defaultValue) {
          costs = await getCosts(line, daily);
          const descripcion = await getDescription(line, costs, daily);

          updateAdapterPP(adapterPP, costs, descripcion, line);
        }

        await setFieldValue(
          'programPoints',
          adapterPP?.length === 0
            ? [
                {
                  idParteDiario: line?.idParteDiario,
                  quantity: line?.cantidad || '',
                  points: '',
                  ...costs
                }
              ]
            : adapterPP
        );

        setLoading(false);
      } catch (error) {
        setLoading(false);
        setNewToast({
          type: 'ERROR',
          message:
            error?.data?.errorMessage || 'No pudimos procesar la solicitud, intente nuevamente.'
        });
      }
    };

    if (show && line?.tienePuntosPrograma) getDataPP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handlePostProgramPoints = async () => {
    try {
      setLoading(true);
      const body = {
        partesDiariosIds: [line?.idParteDiario],
        detalleOperativo: values.programPoints.map((pp) => ({
          idPartePuntoPrograma: pp.idPartePuntoPrograma || 0,
          puntosDePrograma: pp.point?.length === 0 ? 0 : parseInt(pp.points, 10),
          idOperativo: pp.idOperativo || 0,
          claseCosto: pp.cost?.costoClase ?? '',
          fase: pp.cost?.fase ?? '',
          codigoCosto: pp.code?.nombre ?? '',
          subcodigo: pp.subCode?.nombre ?? '',
          descripcion: pp.detail ?? ''
        }))
      };
      const resp = await putApproveDailyWorkReports('v2', body);

      setNewToast({
        type: 'SUCCESS',
        message: resp?.mensaje
      });
      onExit();
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message:
          error?.data?.errorMessage ||
          error?.message ||
          'No pudimos procesar la solicitud, intente nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalGeneric
      backdrop
      infoModal
      onHide={onExit}
      primaryAction={() => {
        handlePostProgramPoints();
      }}
      primaryActionDisabled={false}
      primaryActionLabel="Confirmar Aprobación"
      secondaryAction={onExit}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Datos operativos"
    >
      <ProgramPointList
        disabledInputPP
        disabledInputQuantity
        displayButtons={false}
        displayFixedlabel={false}
        editCSA={editCSA}
        idParteDiario={line?.idParteDiario}
        line={line}
        loading={loading}
        quantityTotalReturn={quantityTotalReturn}
        selectedDate={dateSelect}
        setFieldValue={setFieldValue}
        showAlert={showAlert}
        showTitle
        tipoAsignacion={daily?.tipoAsignacion}
        values={values}
        viewCSA
      />
    </ModalGeneric>
  );
};

export default ModalProgramPointApproval;

ModalProgramPointApproval.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  line: PropTypes.shape({
    idParteDiario: PropTypes.number,
    tienePuntosPrograma: PropTypes.bool,
    cantidad: PropTypes.string
  }).isRequired,
  dateSelect: PropTypes.string.isRequired,
  fetchData: PropTypes.func.isRequired,
  viewCSA: PropTypes.bool,
  editCSA: PropTypes.bool,
  daily: PropTypes.shape({
    tipoAsignacion: PropTypes.number,
    variosValoresOperativos: PropTypes.bool,
    codigoOperativo: PropTypes.string,
    numeroContrato: PropTypes.number,
    posicionSap: PropTypes.string,
    lineaSap: PropTypes.string
  }).isRequired,
  defaultValue: PropTypes.bool
};

ModalProgramPointApproval.defaultProps = {
  viewCSA: false,
  editCSA: false,
  defaultValue: false
};
